import React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { FormSection, FormHeader, FormFooter, RequiredAsterisk, Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { updateInvoiceGroup } from '@atlassian/temp-proxies';
import { UpdateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import {
  InfoPanel,
  useFormSubmit,
  useNotification,
  useSidebar,
  successNotificationBuilder,
  ErrorComponents,
  InflightInvoiceBanner,
  NotificationTypes,
} from '@css-xp/base';
import { HelpContent } from '@css-xp/ppc-common';
import { EditBillingProfilePaneContainer } from './edit-billing-profile-pane.styles';

interface FormData {
  invoiceGroupName: string;
}

interface Props {
  id: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountId: string;
  name: string;
  inflightBannerMessage?: string;
}

const notificationOptions = successNotificationBuilder()
  .withId('update-billing-profile-success')
  .withHeader('Update successful')
  .withMessage('Billing profile updated')
  .withDismissAfter(5000)
  .isVersion(NotificationTypes.FLAG)
  .build();

export const EditBillingProfileNamePane = ({
  id,
  refetch,
  transactionAccountId,
  name,
  inflightBannerMessage = 'All changes will be reflected to the orders included in the inflight invoice',
}: Props): JSX.Element => {
  const { closeSidebar } = useSidebar();

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<UpdateInvoiceGroupRequest, any>({
    submitFormAction: updateInvoiceGroup,
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const handleFormSubmit = (formData: FormData) => {
    const requestData: UpdateInvoiceGroupRequest = {
      data: {
        name: formData?.invoiceGroupName,
      },
      invoiceGroupId: id,
      transactionAccountId: transactionAccountId,
    };

    submitForm(requestData);
  };

  if (complete && !notificationSent) {
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch();
  }

  return (
    <EditBillingProfilePaneContainer data-testid="billing-profile-name-sidebar">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty }) => (
          <form {...formProps}>
            <FormHeader title="Edit billing profile name">
              <p aria-hidden="true">
                Required fields are marked with an asterisk <RequiredAsterisk />
              </p>
            </FormHeader>
            <InflightInvoiceBanner transactionAccountId={transactionAccountId} message={inflightBannerMessage} />
            <FormSection>
              {formError ? (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to update the billing profile for the following reason"
                      message={formError}
                      helpContent={<HelpContent />}
                    />
                  }
                />
              ) : !transactionAccountId ? (
                <InfoPanel
                  type="warning"
                  title={'Cannot update billing profile'}
                  message={'This billing profile is missing transaction-account id'}
                />
              ) : null}
            </FormSection>
            <FormSection>
              <div className="form-component-width">
                <Field
                  name="invoiceGroupName"
                  id="invoiceGroupName"
                  defaultValue={name}
                  label="Billing profile name"
                  isRequired={true}
                >
                  {({ fieldProps }) => (
                    <Textfield {...fieldProps} name="invoiceGroupName" testId="billing-profile-name" />
                  )}
                </Field>
              </div>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="default" onClick={closeSidebar}>
                  Cancel
                </Button>
                <LoadingButton
                  isDisabled={!dirty}
                  appearance="primary"
                  type="submit"
                  testId="update-button"
                  isLoading={isProcessing}
                >
                  Save
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </EditBillingProfilePaneContainer>
  );
};

export default React.memo(EditBillingProfileNamePane);
